import { Box, Input } from "@chakra-ui/react";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import {
  Checkbox,
  colors,
  customEvent,
  HLine,
  HStack,
  LargeButton,
  LARGEL_BUTTON_TYPE,
  VStack,
} from "goi_common";
import { ReactNode, useCallback, useMemo, useState } from "react";
import emailjs from "@emailjs/browser";

export function ConsultReservationModalContent({
  companyname,
  onClose,
}: {
  companyname: string;
  onClose: () => void;
}) {
  const yearmonth = useMemo(() => {
    return dayjs().format("YYYY. MM");
  }, [dayjs]);
  const days = useMemo(() => {
    dayjs.locale("ko");
    const today = dayjs();
    const tmr = today.add(1, "day");
    const ttmr = today.add(2, "day");
    return [
      {
        weekday: today.format("ddd"),
        dayNumber: today.format("D"),
        text: "오늘",
      },
      {
        weekday: tmr.format("ddd"),
        dayNumber: tmr.format("D"),
        text: "내일",
      },
      {
        weekday: ttmr.format("ddd"),
        dayNumber: ttmr.format("D"),
        text: "모레",
      },
    ];
  }, [dayjs]);
  const [currentIndex, setCurrentIndex] = useState(1);

  const [value, setValue] = useState("");

  const questionText = useMemo(() => {
    return (
      yearmonth +
      " " +
      days[currentIndex].weekday +
      " 19시 상담 요청 - " +
      companyname +
      "에서 신청"
    );
  }, [currentIndex]);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(() => {
    if (loading) return;
    customEvent({
      newGtm: {
        path: "parlor_detail",
        trigger_and_target: "send_email_mdl-btn",
      },
    });
    if (currentIndex !== 1) {
      alert("예약 가능한 날짜를 확인하세요.");
      return;
    }
    if (!checked) {
      alert("개인정보 수집 및 이용에 동의해주세요.");
      return;
    }

    if (!value) {
      alert("번호를 입력해주세요.");
      return;
    } else {
      if (/^010-\d{4}-\d{4}$/.test(value)) {
        emailjs
          .send(
            "service_agc6alu",
            "template_o1ul2fg",
            {
              question: questionText,
              phone: value,
            },
            "ckK66U9O1M16QnFJq",
          )
          .then(() => {
            customEvent({
              newGtm: { path: "parlor_detail", trigger_and_target: "send_question_email" },
            });
            alert("상담 예약이 완료되었습니다.");
            onClose();
            setChecked(false);
            setLoading(false);
          })
          .finally(() => setLoading(false));
      } else {
        alert("번호 형식을 확인해주세요. (010-****-****)");
        return;
      }
    }
  }, [checked, value]);

  const onClickReserve = () => {
    handleClick();
  };
  return (
    <VStack padding="24px">
      <VStack gap="16px">
        <Box className="subtitle_16_b" textAlign="center">
          {companyname}
          <br />
          사전 상담 예약
        </Box>
        <Box
          background={colors.gray100}
          padding="12px 16px"
          className="body_14_m"
          css={{
            wordBreak: "keep-all",
          }}
        >
          장례식장 예상 비용, 추천 장례식장을 안내해드립니다. 빈소 할인, 맞춤형 혜택, 행정 및 서류
          절차까지 한 번에 안내해드려요.
          <br />
          <br />
          빠른 상담이 필요하신 경우 <b className="danger">1666-9784</b>에서 24시간 상담가능합니다.
        </Box>
        <VStack padding="12px 0" gap={12}>
          <Box className="subtitle_16_b gray900">날짜와 시간을 선택해주세요.</Box>
          <VStack gap={8}>
            <Box className="subtitle_14_b black">{yearmonth}</Box>
            <HStack alignItems="center" justifyContent="space-between">
              {days.map((d, index) => (
                <VStack
                  key={d.dayNumber}
                  onClick={() => {
                    setCurrentIndex(index);
                  }}
                  padding="8px 0"
                  borderRadius="12px"
                  width="100%"
                  alignItems="center"
                  background={currentIndex === index ? colors.orange650 : ""}
                >
                  <Box className={currentIndex === index ? "body_14_m white" : "body_14_m gray800"}>
                    {d.weekday} / {d.dayNumber}일
                  </Box>
                  {currentIndex === index && <Box className="caption_12_m white">{d.text}</Box>}
                </VStack>
              ))}
            </HStack>
          </VStack>
          <HLine width="100%" height="2px" color={colors.gray300} />
          <HStack justifyContent="space-between" alignItems="center" gap={12}>
            <ReserveBox>
              12:00
              <br />
              (예약완료)
            </ReserveBox>
            <ReserveBox>
              15:00
              <br />
              (예약완료)
            </ReserveBox>

            {currentIndex !== 1 && (
              <ReserveBox>
                19:00
                <br />
                (예약완료)
              </ReserveBox>
            )}
            {currentIndex === 1 && (
              <ReserveBox active>
                19:00
                <br />
                (예약가능)
              </ReserveBox>
            )}
          </HStack>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            border={`1px solid ${colors.gray400}`}
            width="100%"
            placeholder={"010-****-****"}
          />

          <HStack alignItems="center" justifyContent="space-between">
            <Checkbox
              index={2}
              label="개인정보 수집, 이용, 제공 동의"
              checked={checked}
              onChange={(e) => {
                setChecked(!checked);
              }}
            />
            <a
              className="caption_11_b"
              href="https://goifuneral.notion.site/146a79013e6c40a9b4731dd07a8ad99d"
              target="_blank"
            >
              보기
            </a>
          </HStack>
        </VStack>

        <LargeButton
          className="subtitle_16_sb"
          buttonType={LARGEL_BUTTON_TYPE.ORANGE}
          onClick={onClickReserve}
        >
          상담 예약하기
        </LargeButton>
      </VStack>
    </VStack>
  );
}

function ReserveBox({ children, active }: { children: ReactNode; active?: boolean }) {
  return (
    <Box
      width="100%"
      className={active ? "caption_12_b black" : "caption_12_m gray400"}
      borderRadius={4}
      border={`1px solid ${active ? colors.black : colors.gray300}`}
      padding="4px 12px"
      textAlign="center"
    >
      {children}
    </Box>
  );
}
